import { AppNode } from "@features/builder/graph/types"
import { NodesGroupMapper } from "@features/builder/helpers/nodesGroupMapper"
import Fuse, { IFuseOptions } from "fuse.js"
import { get } from "lodash"
import { XYPosition } from "@xyflow/react"

export type SearchNodeType = {
  groupIds: string[] | null
  nodeId: string
  position?: XYPosition
  format?: string
}

export type FuseBody = {
  nodeId: string
  label?: string
  node?: AppNode
  format?: string
  groups?: (string | undefined)[]
}

export type SearchType = "label" | "unit" | "mnemonic" | "description" | "group" | "type" | "link"

function searchFuse(searchTerm: string, keys: string[], items: FuseBody[]) {
  const options: IFuseOptions<FuseBody> = {
    includeScore: true,
    useExtendedSearch: true,
    keys,
  }
  const fuse = new Fuse(items, options)
  const searchTerms = searchTerm.match(/(".*?"|[^"\s]+)+(?=\s*|\s*$)/g) //.map(x=>x.replaceAll('"', ""))
  // we want to allow space to act as AND by default, not fuzzy matching
  // so we prepend ' to each word not already having a modifier
  // BUT- we must protect something in quotes already e.g. ='find this thing'

  //https://www.fusejs.io/examples.html#extended-searchToken
  // White space acts as an AND operator, while a single pipe (|) character acts as an OR operator.
  // Match      type	                           Description
  // jscript	  fuzzy-match	Items                that fuzzy match jscript  [X]
  // =scheme	  exact-match	Items                that are scheme
  // 'python	  include-match	Items              that include python
  // !ruby	    inverse-exact-match	Items        that do not include ruby
  // ^java	    prefix-exact-match Items         that start with java
  // !^earlang	inverse-prefix-exact-match Items that do not start with earlang
  // .js$	      suffix-exact-match Items         that end with .js
  // !.go$	    inverse-suffix-exact-match Items that do not end with .go

  // when the user provides an empty search term, treat it specially:
  // return items where at least one of the provided keys has any value
  const trimmed = searchTerm.trim()
  if (trimmed === "") {
    const matches = items.filter((fuseItem) =>
      keys.some((key) => {
        const valueAtPath = get(fuseItem, key)

        return valueAtPath !== undefined && valueAtPath !== null && valueAtPath !== ""
      }),
    )

    const results = matches.map((matchedItem) => ({ item: matchedItem, score: 0, refIndex: 0 }))
    return results
  }

  // check no starting or ending modifier for normal searches
  const search = searchTerms
    ?.map((regExpMatch) => {
      const firstLetter = regExpMatch[0] ?? ""
      const lastLetter = regExpMatch[regExpMatch.length - 1] ?? ""
      return firstLetter.match(/^[0-9A-Za-z]$/) && lastLetter.match(/^[0-9A-Za-z]$/)
        ? "'" + regExpMatch
        : regExpMatch
    })
    .join(" ")

  if (!search) return []

  return fuse.search(search)
}

/**
 * NAME to search KEYS Map. To be used in searchFuse(_, KEYS,_ )
 */
const searchFuseKeysMap = new Map<SearchType, string[]>([
  ["label", ["label"]],
  ["unit", ["format"]],
  [
    "mnemonic",
    [
      "node.data.metaNode.Calibration Mnem",
      "node.data.metaNode.Scenario Mnem",
      "node.data.metaNode.Forecast Mnem",
      "node.data.metaNode.Historic Mnem",
    ],
  ],
  ["description", ["node.data.metaNode.Description"]],
  ["group", ["node.data.metaNode.groups"]],
  ["type", ["node.type"]],
  ["link", ["node.data.metaNode.Link"]],
])

export function searchNodes(
  mapper: NodesGroupMapper<AppNode>,
  searchType: SearchType,
  searchText: string,
) {
  const newSearchResult: Map<string, SearchNodeType> = new Map()
  const nodesFuse: FuseBody[] = []
  const groupsFuse: FuseBody[] = []

  for (const node of mapper.nodesMap.values()) {
    nodesFuse.push({
      nodeId: node.id,
      node: node,
      // fields needed to search on
      format: node.data.metaNode["Unit Format"],
      label: node.data.label,
      groups: undefined,
    })
  }

  for (const node of mapper.groupsMap.values()) {
    const groupNodeItem = {
      nodeId: node.id,
      node: node,
      // fields needed to search on
      format: undefined,
      label: node.data.label,
      groups: undefined,
    }
    groupsFuse.push(groupNodeItem)

    if (searchType == "type") {
      nodesFuse.push(groupNodeItem)
    }
  }

  const nodeSuggestions = searchFuse(searchText, searchFuseKeysMap.get(searchType) ?? [], nodesFuse)
  const groupSuggestions = searchFuse(searchText, ["label"], groupsFuse)

  // When searching groups, return all nodes within the matched group (including nested subgroups).
  if (searchType === "group") {
    for (const { item } of groupSuggestions) {
      const groupId = item.nodeId
      const nodeIds = mapper.groupToNodesIdsMapDeep.get(groupId) || []
      for (const nodeId of nodeIds) {
        const node = mapper.nodesMap.get(nodeId)
        newSearchResult.set(nodeId, {
          groupIds: [groupId],
          nodeId: nodeId,
          position: node?.position,
          format: node?.data?.metaNode?.["Unit Format"],
        })
      }
    }
  } else {
    for (const { item } of nodeSuggestions) {
      newSearchResult.set(item.nodeId, {
        groupIds: null,
        nodeId: item.nodeId,
        position: item.node?.position,
        format: item.node?.type !== "group" ? item.node?.data.metaNode["Unit Format"] : undefined,
      })
    }
  }

  return [...newSearchResult.values()]
}
